import React, { useState, useMemo } from "react";

import { useIntl } from "react-intl";

import * as Table from "ds/Table";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import { useLoadDeploymentsCurrent } from "Reducers/environment/deployment/hooks";
import { useCurrentService } from "Reducers/environment/hooks/useCurrentService";
import { useCurrentWorker } from "Reducers/environment/hooks/useCurrentWorker";
import { AppRelationshipsTransformedType } from "Reducers/environment/service";
import EmptyCard from "src/organization/pages/project/environment/services/components/EmptyCard";

import Relationship from "./Relationship";
import * as S from "./Relationships.styles";

const Relationships = () => {
  const intl = useIntl();
  const currentService = useCurrentService();
  const currentWorker = useCurrentWorker();

  const [sortList, setSortList] = useState<{
    by: "name" | "spec";
    order: "asc" | "desc";
  }>({ by: "name", order: "asc" });

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();
  const environment = useSelectorWithUrlParams(environmentSelector, {
    environmentId
  });

  const relationships = currentService
    ? currentService.relationships
    : currentWorker?.relationships;

  const { currentDeployment } = useLoadDeploymentsCurrent(
    { organizationId, projectId, environmentId },
    !!environment?.has_code
  );

  const allServices = currentDeployment?.services;

  const relationshipsList = useMemo(() => {
    if (!relationships) return [];

    return Object.entries(relationships).map(([name, value]) => ({
      name,
      service: value.service,
      serviceObject: allServices?.[value.service],
      endpoint: value.endpoint
    }));
  }, [relationships, allServices]);

  const relationshipsListSorted = useMemo(() => {
    const sortRelationships = (
      a: AppRelationshipsTransformedType,
      b: AppRelationshipsTransformedType
    ) => {
      const { by, order } = sortList;
      const valA = typeof a[by] === "string" ? a[by].toLowerCase() : a[by];
      const valB = typeof b[by] === "string" ? b[by].toLowerCase() : b[by];
      if (order === "asc") {
        if (valA < valB) return -1;
        if (valA > valB) return 1;
        return 0;
      } else {
        if (valA > valB) return -1;
        if (valA < valB) return 1;
        return 0;
      }
    };

    return [...relationshipsList].sort(sortRelationships);
  }, [relationshipsList, sortList]);

  const sortable = relationshipsList.length > 1;

  const handleSortBy = by => {
    if (!sortable) return;
    setSortList({
      by,
      order:
        sortList.by === by ? (sortList.order === "asc" ? "desc" : "asc") : "asc"
    });
  };

  const hasNoRelationships = useMemo(
    () => Object.keys(relationships ?? {}).length === 0,
    [relationships]
  );

  return (
    <S.Wrapper>
      <S.TitleHead tag="h3">
        {intl.formatMessage(
          {
            id: "services.relationships.title"
          },
          {
            count: relationshipsListSorted.length,
            b: txt => (
              <S.BodyText size="medium" $color="granite">
                {txt}
              </S.BodyText>
            )
          }
        )}
      </S.TitleHead>
      {!relationships || hasNoRelationships ? (
        <EmptyCard>
          {intl.formatMessage({ id: "services.relationships.none" })}
        </EmptyCard>
      ) : (
        <S.TableTable>
          <S.TableThead>
            {["name", "endpoint"].map(field => (
              <Table.Th
                key={`header-${field}`}
                onSortChanged={() => {
                  handleSortBy(field);
                }}
                isSortActive={sortList.by === field}
                sortDirection={sortList.order}
              >
                {capitalize(intl.formatMessage({ id: field }))}{" "}
              </Table.Th>
            ))}
            <Table.Th>{capitalize(intl.formatMessage({ id: "ssh" }))}</Table.Th>
          </S.TableThead>
          <S.Hr />
          <S.TableThead>
            {relationshipsListSorted.map(
              (relationship: AppRelationshipsTransformedType) => (
                <Relationship
                  key={relationship.name}
                  relationship={relationship}
                />
              )
            )}
          </S.TableThead>
        </S.TableTable>
      )}
    </S.Wrapper>
  );
};

export default Relationships;
